import { useMemo, type ReactNode } from 'react';
import { CaretDown, Plus, Stack, Trash, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { useGetLists } from '@/modules/lists/hooks/use-lists';
import type {
  SegmentCondition,
  SegmentFieldDefinition,
  SegmentRuleGroup,
  SegmentSubGroup,
} from '../types/segments.types';
import {
  applyFieldChangeToCondition,
  canAddNestedGroup,
  createEmptySubGroup,
  emptyConditionForField,
} from '../utils/segment-rule-tree';

type SegmentCombinator = SegmentRuleGroup['combinator'];
type RuleLevel = SegmentRuleGroup | SegmentSubGroup;

const SOURCE_ORDER = ['contact', 'attribute', 'list', 'event'] as const;

const SOURCE_LABELS: Record<string, string> = {
  contact: 'Contact',
  attribute: 'Custom attributes',
  list: 'Lists',
  event: 'Events',
};

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  notcontains: 'does not contain',
  startswith: 'starts with',
  endswith: 'ends with',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is any of',
  notin: 'is none of',
  isset: 'is set',
  isnotset: 'is not set',
  before: 'is before',
  after: 'is after',
  inlast: 'in the last',
};

const UNIT_OPTIONS = ['days', 'weeks', 'months'] as const;

const LIST_MEMBERSHIP_FIELD = 'list.membership';

export type SegmentRuleCanvasProps = {
  value: SegmentRuleGroup;
  onChange: (next: SegmentRuleGroup) => void;
  fields: SegmentFieldDefinition[];
  highlightedFields?: string[];
  highlightedGroupIndexes?: number[];
};

type ValueOption = { value: string; label: string };

function groupFieldsBySource(fields: SegmentFieldDefinition[]) {
  const buckets = new Map<string, SegmentFieldDefinition[]>();
  for (const field of fields) {
    const source = field.source || 'contact';
    const list = buckets.get(source) ?? [];
    list.push(field);
    buckets.set(source, list);
  }

  const ordered: { source: string; label: string; fields: SegmentFieldDefinition[] }[] = [];
  for (const source of SOURCE_ORDER) {
    const items = buckets.get(source);
    if (!items?.length) continue;
    ordered.push({ source, label: SOURCE_LABELS[source], fields: items });
    buckets.delete(source);
  }
  for (const [source, items] of buckets) {
    ordered.push({ source, label: SOURCE_LABELS[source] ?? source, fields: items });
  }
  return ordered;
}

function fieldByName(
  fields: SegmentFieldDefinition[],
  name: string
): SegmentFieldDefinition | undefined {
  return fields.find((field) => field.field === name);
}

function joinWord(combinator: SegmentCombinator): string {
  return combinator === 'or' ? 'or' : 'and';
}

function mapGroup(
  root: SegmentRuleGroup,
  groupIndex: number | null,
  fn: (group: RuleLevel) => RuleLevel
): SegmentRuleGroup {
  if (groupIndex === null) {
    const next = fn(root) as SegmentRuleGroup;
    return {
      combinator: next.combinator,
      conditions: next.conditions,
      groups: next.groups ?? root.groups,
    };
  }

  return {
    ...root,
    groups: root.groups.map((group, index) => {
      if (index !== groupIndex) return group;
      const next = fn(group);
      return { combinator: next.combinator, conditions: next.conditions };
    }),
  };
}

function applyOperatorChange(
  condition: SegmentCondition,
  operator: string,
  field: SegmentFieldDefinition | undefined
): SegmentCondition {
  const next: SegmentCondition = { field: condition.field, operator };

  if (operator === 'isset' || operator === 'isnotset') {
    return next;
  }

  if (operator === 'inlast') {
    const numeric =
      typeof condition.value === 'number' &&
      Number.isInteger(condition.value) &&
      condition.value > 0
        ? condition.value
        : undefined;
    return {
      ...next,
      ...(numeric !== undefined ? { value: numeric } : {}),
      unit: condition.unit && condition.unit.length > 0 ? condition.unit : 'days',
    };
  }

  if (operator === 'in' || operator === 'notin') {
    if (Array.isArray(condition.value)) {
      return { ...next, value: condition.value };
    }
    if (condition.value !== undefined && condition.value !== null && condition.value !== '') {
      return { ...next, value: [condition.value] };
    }
    return { ...next, value: [] };
  }

  const raw = Array.isArray(condition.value) ? condition.value[0] : condition.value;
  if (raw === undefined || raw === null || raw === '') {
    return next;
  }
  if (field?.dataType === 'boolean' && typeof raw !== 'boolean') {
    return next;
  }
  return { ...next, value: raw };
}

function parseNumberInput(raw: string): number | undefined {
  if (raw === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function dateInputValue(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, 10) : '';
}

function selectedStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

function selectedNumbers(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is number => typeof entry === 'number' && Number.isFinite(entry));
}

export function SegmentRuleCanvas({
  value,
  onChange,
  fields,
  highlightedFields = [],
  highlightedGroupIndexes = [],
}: SegmentRuleCanvasProps) {
  const needsLists = fields.some(
    (field) => field.field === LIST_MEMBERSHIP_FIELD || field.source === 'list'
  );
  // First page only (pageSize 100) — same cap as lists-module add-to-list; search pagination is YAGNI for now.
  const { data: listsData } = useGetLists({ pageNo: 1, pageSize: 100 }, { enabled: needsLists });

  const listOptions = useMemo<ValueOption[]>(
    () => (listsData?.items ?? []).map((list) => ({ value: list.id, label: list.name })),
    [listsData?.items]
  );

  const fieldGroups = useMemo(() => groupFieldsBySource(fields), [fields]);
  const firstField = fields[0];
  const highlighted = useMemo(() => new Set(highlightedFields), [highlightedFields]);
  const highlightedGroups = useMemo(
    () => new Set(highlightedGroupIndexes),
    [highlightedGroupIndexes]
  );

  const addCondition = (groupIndex: number | null) => {
    if (!firstField) return;
    onChange(
      mapGroup(value, groupIndex, (group) => ({
        ...group,
        conditions: [...group.conditions, emptyConditionForField(firstField)],
      }))
    );
  };

  const addGroup = () => {
    if (!canAddNestedGroup(0)) return;
    onChange({
      ...value,
      groups: [...value.groups, createEmptySubGroup()],
    });
  };

  const setCombinator = (groupIndex: number | null, combinator: SegmentCombinator) => {
    onChange(mapGroup(value, groupIndex, (group) => ({ ...group, combinator })));
  };

  const patchCondition = (groupIndex: number | null, condIndex: number, next: SegmentCondition) => {
    onChange(
      mapGroup(value, groupIndex, (group) => ({
        ...group,
        conditions: group.conditions.map((condition, index) =>
          index === condIndex ? next : condition
        ),
      }))
    );
  };

  const removeCondition = (groupIndex: number | null, condIndex: number) => {
    onChange(
      mapGroup(value, groupIndex, (group) => ({
        ...group,
        conditions: group.conditions.filter((_, index) => index !== condIndex),
      }))
    );
  };

  const removeGroup = (groupIndex: number) => {
    onChange({
      ...value,
      groups: value.groups.filter((_, index) => index !== groupIndex),
    });
  };

  const rootItems: Array<{ kind: 'condition'; index: number } | { kind: 'group'; index: number }> =
    [
      ...value.conditions.map((_, index) => ({ kind: 'condition' as const, index })),
      ...value.groups.map((_, index) => ({ kind: 'group' as const, index })),
    ];

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white px-4 py-4 pr-5 shadow-e1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-neutral-700">Contacts match</span>
        <CombinatorControl
          combinator={value.combinator}
          label="How the items below combine"
          onChange={(combinator) => setCombinator(null, combinator)}
        />
        <span className="text-neutral-700">of the items below</span>
      </div>

      <RuleRail nested={false}>
        {rootItems.length === 0 ? (
          <p className="py-2 text-xs text-neutral-500">No conditions yet.</p>
        ) : (
          rootItems.map((item, railIndex) => {
            const isFirst = railIndex === 0;
            const join = isFirst ? 'Where' : joinWord(value.combinator);
            if (item.kind === 'condition') {
              const condition = value.conditions[item.index];
              return (
                <RuleItem key={`c-${item.index}`} join={join} isFirst={isFirst}>
                  <ConditionRow
                    condition={condition}
                    index={item.index}
                    fieldGroups={fieldGroups}
                    fields={fields}
                    listOptions={listOptions}
                    highlighted={highlighted.has(condition.field)}
                    onChange={(next) => patchCondition(null, item.index, next)}
                    onRemove={() => removeCondition(null, item.index)}
                  />
                </RuleItem>
              );
            }
            const group = value.groups[item.index];
            return (
              <RuleItem key={`g-${item.index}`} join={join} isFirst={isFirst} isGroup>
                <NestedGroupCard
                  group={group}
                  groupIndex={item.index}
                  fieldGroups={fieldGroups}
                  fields={fields}
                  listOptions={listOptions}
                  highlighted={highlighted}
                  isGroupHighlighted={highlightedGroups.has(item.index)}
                  onCombinatorChange={(combinator) => setCombinator(item.index, combinator)}
                  onConditionChange={(condIndex, next) =>
                    patchCondition(item.index, condIndex, next)
                  }
                  onRemoveCondition={(condIndex) => removeCondition(item.index, condIndex)}
                  onAddCondition={() => addCondition(item.index)}
                  onRemoveGroup={() => removeGroup(item.index)}
                />
              </RuleItem>
            );
          })
        )}
      </RuleRail>

      <div className="flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-primary-600"
          onClick={() => addCondition(null)}
        >
          <Plus size={14} />
          Add condition
        </Button>
        {canAddNestedGroup(0) ? (
          <Button type="button" variant="outline" size="sm" onClick={addGroup}>
            <Stack size={14} />
            Add group
          </Button>
        ) : null}
        <span className="text-xs text-neutral-500">
          Groups nest one level deep, and each group has one combinator — the canvas can only draw
          rules the API accepts.
        </span>
      </div>
    </div>
  );
}

function CombinatorControl({
  combinator,
  label,
  onChange,
}: {
  combinator: SegmentCombinator;
  label: string;
  onChange: (combinator: SegmentCombinator) => void;
}) {
  return (
    <span
      className="inline-flex overflow-hidden rounded-md border border-neutral-200 bg-white"
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        aria-pressed={combinator === 'and'}
        className={cn(
          'h-[30px] px-3 text-[13px] font-semibold',
          combinator === 'and'
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-white text-neutral-900 hover:bg-neutral-50'
        )}
        onClick={() => onChange('and')}
      >
        All (AND)
      </button>
      <button
        type="button"
        aria-pressed={combinator === 'or'}
        className={cn(
          'h-[30px] border-l border-neutral-200 px-3 text-[13px] font-semibold',
          combinator === 'or'
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-white text-neutral-900 hover:bg-neutral-50'
        )}
        onClick={() => onChange('or')}
      >
        Any (OR)
      </button>
    </span>
  );
}

function RuleRail({ nested, children }: { nested: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'relative flex flex-col before:absolute before:bottom-3.5 before:top-3.5 before:w-px before:bg-neutral-200',
        nested ? 'before:left-1' : 'before:left-1.5'
      )}
      data-nested={nested ? 'true' : undefined}
    >
      {children}
    </div>
  );
}

function RuleItem({
  join,
  isFirst,
  isGroup = false,
  nested = false,
  children,
}: {
  join: string;
  isFirst: boolean;
  isGroup?: boolean;
  nested?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'grid items-start gap-x-2',
        /* Wide enough for "Where"; labels are end-aligned so short joins sit next to fields */
        nested ? 'grid-cols-[48px_minmax(0,1fr)]' : 'grid-cols-[52px_minmax(0,1fr)]',
        isGroup && 'py-2.5'
      )}
    >
      <span
        className={cn(
          'mt-2 flex h-10 items-center justify-end whitespace-nowrap text-xs font-semibold',
          isFirst ? 'text-neutral-400' : 'text-neutral-500'
        )}
      >
        {join}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function NestedGroupCard({
  group,
  groupIndex,
  fieldGroups,
  fields,
  listOptions,
  highlighted,
  isGroupHighlighted,
  onCombinatorChange,
  onConditionChange,
  onRemoveCondition,
  onAddCondition,
  onRemoveGroup,
}: {
  group: SegmentSubGroup;
  groupIndex: number;
  fieldGroups: ReturnType<typeof groupFieldsBySource>;
  fields: SegmentFieldDefinition[];
  listOptions: ValueOption[];
  highlighted: Set<string>;
  isGroupHighlighted: boolean;
  onCombinatorChange: (combinator: SegmentCombinator) => void;
  onConditionChange: (condIndex: number, next: SegmentCondition) => void;
  onRemoveCondition: (condIndex: number) => void;
  onAddCondition: () => void;
  onRemoveGroup: () => void;
}) {
  const title = `Group ${groupIndex + 1}`;
  return (
    <div
      data-testid="segment-nested-group"
      data-highlighted={isGroupHighlighted ? 'true' : undefined}
      className={cn(
        'rounded-lg border border-neutral-200 bg-neutral-50 p-4',
        isGroupHighlighted && 'ring-2 ring-destructive/40'
      )}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="font-medium text-neutral-900">{title}</span>
        <span className="text-xs text-neutral-500">Match</span>
        <CombinatorControl
          combinator={group.combinator}
          label={`How the conditions in ${title.toLowerCase()} combine`}
          onChange={onCombinatorChange}
        />
        <span className="text-xs text-neutral-500">of the following</span>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-destructive"
          aria-label={`Remove group ${groupIndex + 1}`}
          onClick={onRemoveGroup}
        >
          <Trash size={16} />
        </Button>
      </div>
      <RuleRail nested>
        {group.conditions.length === 0 ? (
          <p className="py-2 text-xs text-neutral-500">No conditions in this group yet.</p>
        ) : (
          group.conditions.map((condition, index) => (
            <RuleItem
              key={index}
              join={index === 0 ? 'Where' : joinWord(group.combinator)}
              isFirst={index === 0}
              nested
            >
              <ConditionRow
                condition={condition}
                index={index}
                fieldGroups={fieldGroups}
                fields={fields}
                listOptions={listOptions}
                highlighted={highlighted.has(condition.field)}
                onChange={(next) => onConditionChange(index, next)}
                onRemove={() => onRemoveCondition(index)}
              />
            </RuleItem>
          ))
        )}
      </RuleRail>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 text-primary-600"
        onClick={onAddCondition}
      >
        <Plus size={14} />
        Add condition
      </Button>
    </div>
  );
}

function ConditionRow({
  condition,
  index,
  fieldGroups,
  fields,
  listOptions,
  highlighted,
  onChange,
  onRemove,
}: {
  condition: SegmentCondition;
  index: number;
  fieldGroups: ReturnType<typeof groupFieldsBySource>;
  fields: SegmentFieldDefinition[];
  listOptions: ValueOption[];
  highlighted: boolean;
  onChange: (next: SegmentCondition) => void;
  onRemove: () => void;
}) {
  const meta = fieldByName(fields, condition.field);
  const wideValue = condition.operator === 'in' || condition.operator === 'notin';
  const knownFieldValues = new Set(fields.map((field) => field.field));

  return (
    <div
      data-testid="segment-condition-row"
      data-highlighted={highlighted ? 'true' : undefined}
      className={cn(
        'flex flex-col gap-1.5 py-2 pr-1',
        highlighted && 'rounded-md bg-destructive/5 ring-2 ring-destructive/40'
      )}
    >
      <div className="rule-body">
        <div className="cond-grid" data-wide-value={wideValue ? 'true' : undefined}>
          <div data-cell="field">
            <Select
              value={condition.field || undefined}
              onValueChange={(fieldName) => {
                const nextField = fieldByName(fields, fieldName);
                if (!nextField) return;
                onChange(applyFieldChangeToCondition(condition, nextField));
              }}
            >
              <SelectTrigger className="h-10" aria-label={`Condition ${index + 1}: field`}>
                <SelectValue placeholder="Field" />
              </SelectTrigger>
              <SelectContent>
                {!knownFieldValues.has(condition.field) && condition.field ? (
                  <SelectItem
                    value={condition.field}
                  >{`Unknown field: ${condition.field}`}</SelectItem>
                ) : null}
                {fieldGroups.map((group) => (
                  <SelectGroup key={group.source}>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.fields.map((field) => (
                      <SelectItem key={field.field} value={field.field}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div data-cell="op">
            {meta ? (
              <Select
                value={condition.operator || undefined}
                onValueChange={(operator) =>
                  onChange(applyOperatorChange(condition, operator, meta))
                }
              >
                <SelectTrigger className="h-10" aria-label={`Condition ${index + 1}: operator`}>
                  <SelectValue placeholder="Operator" />
                </SelectTrigger>
                <SelectContent>
                  {(meta.operators.includes(condition.operator)
                    ? meta.operators
                    : [condition.operator, ...meta.operators]
                  ).map((operator) => (
                    <SelectItem key={operator} value={operator}>
                      {OPERATOR_LABELS[operator] ?? operator}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>

          <div data-cell="value">
            <ConditionValue
              condition={condition}
              meta={meta}
              listOptions={listOptions}
              onChange={onChange}
            />
          </div>

          <div data-cell="remove">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-8 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              aria-label={`Remove condition ${index + 1}`}
              onClick={onRemove}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConditionValue({
  condition,
  meta,
  listOptions,
  onChange,
}: {
  condition: SegmentCondition;
  meta: SegmentFieldDefinition | undefined;
  listOptions: ValueOption[];
  onChange: (next: SegmentCondition) => void;
}) {
  if (!meta) {
    return (
      <p className="py-2 text-xs text-neutral-500">
        This attribute was removed — choose a different field, or remove this condition.
      </p>
    );
  }

  const operator = condition.operator;
  if (operator === 'isset' || operator === 'isnotset') {
    return <p className="pt-2.5 text-xs text-neutral-500">No value needed.</p>;
  }

  const setValue = (value: unknown | undefined, unit?: string | null) => {
    const next: SegmentCondition = { field: condition.field, operator: condition.operator };
    if (value !== undefined) next.value = value;
    if (unit !== undefined) {
      if (unit === null) {
        delete next.unit;
      } else {
        next.unit = unit;
      }
    } else if (condition.unit) {
      next.unit = condition.unit;
    }
    onChange(next);
  };

  if (operator === 'inlast' || meta.dataType === 'event') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={1}
          className="h-10 max-w-[100px]"
          aria-label="Condition value"
          value={typeof condition.value === 'number' ? String(condition.value) : ''}
          onChange={(event) =>
            setValue(parseNumberInput(event.target.value), condition.unit ?? 'days')
          }
        />
        <Select
          value={condition.unit ?? 'days'}
          onValueChange={(unit) => setValue(condition.value, unit)}
        >
          <SelectTrigger className="h-10 max-w-[120px]" aria-label="Condition value unit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNIT_OPTIONS.map((unit) => (
              <SelectItem key={unit} value={unit}>
                {unit}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (operator === 'in' || operator === 'notin') {
    const options =
      meta.field === LIST_MEMBERSHIP_FIELD || meta.dataType === 'list'
        ? listOptions
        : (meta.enumOptions ?? []).map((option) => ({ value: option, label: option }));
    if (options.length === 0 && meta.dataType !== 'list' && meta.field !== LIST_MEMBERSHIP_FIELD) {
      if (meta.dataType === 'number') {
        const joined = selectedNumbers(condition.value).join(', ');
        return (
          <Input
            type="text"
            className="h-10"
            aria-label="Condition value"
            placeholder="e.g. 10, 20, 30"
            value={joined}
            onChange={(event) =>
              setValue(
                event.target.value
                  .split(',')
                  .map((part) => part.trim())
                  .filter(Boolean)
                  .map(Number)
                  .filter((entry) => Number.isFinite(entry))
              )
            }
          />
        );
      }

      const joined = selectedStrings(condition.value).join(', ');
      return (
        <Input
          type="text"
          className="h-10"
          aria-label="Condition value"
          placeholder="e.g. Zurich, Bern"
          value={joined}
          onChange={(event) =>
            setValue(
              event.target.value
                .split(',')
                .map((part) => part.trim())
                .filter(Boolean)
            )
          }
        />
      );
    }
    return (
      <MultiValuePicker
        options={options}
        selected={selectedStrings(condition.value)}
        onChange={(next) => setValue(next)}
      />
    );
  }

  if (meta.dataType === 'boolean') {
    const current =
      condition.value === true ? 'true' : condition.value === false ? 'false' : undefined;
    return (
      <Select value={current} onValueChange={(next) => setValue(next === 'true')}>
        <SelectTrigger className="h-10" aria-label="Condition value">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (meta.dataType === 'date') {
    return (
      <Input
        type="date"
        className="h-10"
        aria-label="Condition value"
        value={dateInputValue(condition.value)}
        onChange={(event) => setValue(event.target.value || undefined)}
      />
    );
  }

  if (meta.dataType === 'number') {
    return (
      <Input
        type="number"
        className="h-10"
        aria-label="Condition value"
        value={typeof condition.value === 'number' ? String(condition.value) : ''}
        onChange={(event) => setValue(parseNumberInput(event.target.value))}
      />
    );
  }

  if (meta.dataType === 'enum' || meta.dataType === 'status') {
    const options = meta.enumOptions ?? [];
    const current = typeof condition.value === 'string' ? condition.value : undefined;
    return (
      <Select value={current} onValueChange={(next) => setValue(next)}>
        <SelectTrigger className="h-10" aria-label="Condition value">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type="text"
      className="h-10"
      aria-label="Condition value"
      placeholder="Value"
      value={typeof condition.value === 'string' ? condition.value : ''}
      onChange={(event) => setValue(event.target.value || undefined)}
    />
  );
}

function MultiValuePicker({
  options,
  selected,
  onChange,
}: {
  options: ValueOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const labelByValue = new Map(options.map((option) => [option.value, option.label]));

  const toggle = (value: string, checked: boolean) => {
    if (checked) {
      onChange(selected.includes(value) ? selected : [...selected, value]);
      return;
    }
    onChange(selected.filter((entry) => entry !== value));
  };

  return (
    <Popover>
      <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input px-2 py-1">
        {selected.length === 0 ? (
          <span className="text-sm text-neutral-400">Select…</span>
        ) : (
          selected.map((value) => {
            const label = labelByValue.get(value) ?? value;
            return (
              <span
                key={value}
                className="inline-flex max-w-full items-center gap-0.5 rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800"
              >
                <span className="truncate">{label}</span>
                <button
                  type="button"
                  className="inline-flex size-[18px] items-center justify-center rounded-sm hover:bg-primary-100"
                  aria-label={`Remove ${label}`}
                  onClick={() => toggle(value, false)}
                >
                  ×
                </button>
              </span>
            );
          })
        )}
        <PopoverTrigger asChild>
          <button
            type="button"
            className="ml-auto inline-flex size-5 items-center justify-center rounded-sm text-neutral-500"
            aria-label="Choose values"
            aria-haspopup="listbox"
          >
            <CaretDown size={14} />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        align="start"
        className="max-h-64 w-[var(--radix-popover-trigger-width)] min-w-[220px] overflow-auto p-1"
      >
        {options.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-neutral-500">No values available.</p>
        ) : (
          options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  checked={checked}
                  aria-label={option.label}
                  onCheckedChange={(state) => toggle(option.value, state === true)}
                />
                <span>{option.label}</span>
              </label>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
